import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

/**
 * Tema em memória apenas.
 *
 * O CSS já cobre claro e escuro via `prefers-color-scheme`, então a página abre
 * correta sem nenhum JavaScript e sem piscar. Este hook só existe para o botão
 * de troca manual, que vale enquanto a aba estiver aberta — coerente com a
 * promessa de não gravar nada no aparelho.
 */
export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })
  const [overridden, setOverridden] = useState(false)

  // Enquanto o usuário não escolher, seguimos o sistema em tempo real.
  useEffect(() => {
    if (overridden || !window.matchMedia) return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setTheme(event.matches ? 'dark' : 'light')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [overridden])

  useEffect(() => {
    if (!overridden) {
      document.documentElement.removeAttribute('data-theme')
      return
    }
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme, overridden])

  const toggleTheme = useCallback(() => {
    setOverridden(true)
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, toggleTheme }
}
